<template>
  <div>
    <!-- Guest Form -->
    <BottomWrapper
      v-if="currentStep === 'form'"
      id="rule-engine-bottom-wrapper"
      v-waiting.center="isLoading = { isLoading: loading }"
      :buttons="buttons"
      cancelable-request
      @select="handleSubmit"
      @support-request:cancel="$emit('support-request:cancel', $event)"
    >
      <div class="form">
        <div class="center title-wrapper">
          <div>
            {{
              isOpenRequestFlow ? Locale.i18n.t('page.guest.verify_details') : Locale.i18n.t('page.guest.enter_details')
            }}
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="error-message">
          <div class="error-content">
            <div class="error-icon">X</div>
            <div class="error-text">
              {{ errorMessage }}
            </div>
            <button class="error-close" @click="clearError">×</button>
          </div>
        </div>

        <GuestFormContent
          v-model="form"
          :errors="errors"
          :show-description-box="showDescriptionBox"
          :description-message="descriptionPlaceholder"
        />
      </div>
    </BottomWrapper>

    <!-- OTP Verification -->
    <OtpVerification
      v-else-if="currentStep === 'otp'"
      :guest-data="guestData"
      :on-verify-success="handleOtpVerification"
      :on-resend-otp="resendOtp"
      @change-email="goToForm"
      @back="goToForm"
      @support-request:cancel="$emit('support-request:cancel', $event)"
    />

    <!-- Confirmation Screen -->
    <TicketConfirmation
      v-else-if="currentStep === 'confirmation'"
      :ticket-id="createdTicketId"
      :email="form.email"
      @close="handleClose"
      @support-request:cancel="$emit('support-request:cancel', $event)"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { BottomWrapper } from '../../BottomWrapper.vue';
import waiting from '@/components/loading/waiting';
import { isEmailValid, isPhoneValid } from '@/common/utils';
import * as PleasedApi from '@/common/api/api.pleased';
import { IUpdatePleasedUser, VisitorInfoFilled, VisitorInformation } from '@/common/types';
import { namespace } from 'vuex-class';
import { MAX_GUEST_FIELDS_CHAR, MIN_DESCRIPTION_CHAR_NUM } from '@/common/config';
import { UserInformation } from '@/common/api/models.pleased';
import { ConnectToAgent } from '@/common/bot/types';
import * as Locale from '@/plugins/i18n';
import GuestFormContent from './GuestFormContent.vue';
import OtpVerification from '@/components/OtpVerification.vue';
import TicketConfirmation from '@/components/TicketConfirmation.vue';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { mixins } from 'vue-class-component';
import { analytics } from '@/common/logs';
import { GENERAL_EVENTS } from '@/common/logs/events';
import { reportingService } from '@/common/reporting';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';

const userModule = namespace('user');

interface TicketForm {
  name: string;
  email: string;
  description: string;
  phone: string;
}

@Component({
  components: {
    BottomWrapper,
    GuestFormContent,
    OtpVerification,
    TicketConfirmation,
  },
  directives: {
    waiting,
  },
})
export default class GuestForm extends mixins(LogPageEventMixin) {
  @userModule.Mutation('updatePleasedUser') updatePleasedUser!: (payload: IUpdatePleasedUser) => void;
  @userModule.Mutation('updateGuestUser') updateGuestUser!: (payload: UserInformation) => void;
  @userModule.Mutation('clearGuestFormData') clearGuestFormData!: () => void;
  @Prop({ default: null }) readonly guestSavedData!: VisitorInfoFilled | null;
  @Prop() readonly connectConfig!: ConnectToAgent | null;
  @Prop({ default: false }) readonly isOpenRequestFlow!: boolean;
  @Prop() readonly requestInfo!: { subject: string };
  readonly Locale = Locale;
  readonly [PAGE_TRACKING] = PAGE_EVENTS.CONNECT_REQUEST_GUEST;

  loading = false;
  MAX_GUEST_FIELDS_CHAR = MAX_GUEST_FIELDS_CHAR;
  currentStep: 'form' | 'otp' | 'confirmation' = 'form';
  createdTicketId = '';
  errorMessage = '';
  originalEmail = ''; // Track the email when OTP was first sent

  get isDescriptionExceed() {
    return this.form.description.length > MAX_GUEST_FIELDS_CHAR.description;
  }

  get showDescriptionBox() {
    // Hide description box for first time (Continue as Guest)
    if (!this.isOpenRequestFlow) {
      return false;
    }

    // Show description box for second time (Open Request flow)
    return true;
  }

  get descriptionPlaceholder() {
    return this.connectConfig?.descriptionMessage;
  }

  get guestData() {
    return {
      name: this.form.name,
      email: this.form.email,
      phone: this.form.phone,
      description: this.form.description,
    };
  }

  get buttons() {
    return [
      {
        name: Locale.i18n.t('page.guest.button.proceed'),
        id: '',
        isActive: true,
      },
    ];
  }

  form: TicketForm = {
    name: '',
    phone: '',
    email: '',
    description: '',
  };

  errors = {
    name: false,
    email: false,
    description: false,
    phone: false,
  };

  created() {
    document.title = 'Guest Form';
    if (this.guestSavedData) {
      this.form = {
        name: this.guestSavedData.visitorInfo.name || '',
        phone: this.guestSavedData.visitorInfo.phone || '',
        email: this.guestSavedData.visitorInfo.email || '',
        description: this.guestSavedData.description || '',
      };
    }
  }

  async sendOtp(options?: { stayOnOtpStep?: boolean }) {
    this.loading = true;
    this.errorMessage = ''; // Clear any previous errors

    // Track OTP send attempt
    analytics.track({
      name: GENERAL_EVENTS.GUEST_OTP_SEND_ATTEMPT,
      metadata: {
        email: this.form.email,
        name: this.form.name,
      },
    });

    try {
      await PleasedApi.sendOtpToEmail(this.form.email, {
        name: this.form.name,
      });

      // Track successful OTP send
      analytics.track({
        name: GENERAL_EVENTS.GUEST_OTP_SEND_SUCCESS,
        metadata: {
          email: this.form.email,
        },
      });

      // Store the original email for comparison later
      this.originalEmail = this.form.email;
      this.currentStep = 'otp';
    } catch (error: any) {
      // Track OTP send failure
      analytics.trackCriticalEvent({
        name: GENERAL_EVENTS.GUEST_OTP_SEND_FAILED,
        metadata: {
          email: this.form.email,
          error: error.message || 'Unknown error',
        },
      });
      captureEventException(error, ErrorTrackingEvent.GuestForm);

      if (error.message) {
        this.errorMessage = error.message;
      } else {
        this.errorMessage = 'Failed to send OTP. Please try again.';
      }

      if (!options?.stayOnOtpStep) {
        // Fallback to form step if OTP fails on initial send
        this.goToForm();
      }
      throw error;
    } finally {
      this.loading = false;
    }
  }

  resendOtp = () => this.sendOtp({ stayOnOtpStep: true });

  validate() {
    let result = true;
    if (this.form.phone && !isPhoneValid(this.form.phone)) {
      this.errors.phone = true;
      result = false;
    }
    if (!isEmailValid(this.form.email)) {
      this.errors.email = true;
      result = false;
    }

    const textLength = this.form.description.length;
    if (this.showDescriptionBox && (textLength < MIN_DESCRIPTION_CHAR_NUM || this.isDescriptionExceed)) {
      this.errors.description = true;
      result = false;
    }
    if (!this.form.name.length) {
      this.errors.name = true;
      result = false;
    }
    return result;
  }

  processGuestData(userData: UserInformation) {
    const config = {
      ...userData,
      isGuest: true,
      name: this.form.name || userData.name || '',
      email: this.form.email || userData.email || '',
      phone: this.form.phone || userData.phone || '',
    };

    this.updateGuestUser(config);
    this.updatePleasedUser(config);

    const event: VisitorInfoFilled = {
      visitorInfo: {
        displayName: this.form.name,
        email: this.form.email,
        name: this.form.name,
        phone: this.form.phone,
        isGuest: true,
      },
      description: this.form.description,
      userData,
    };
    this.$emit('submit:detail', event);
  }

  async handleSubmit() {
    if (!this.validate()) {
      return;
    }

    // Clear any previous errors
    this.errorMessage = '';

    // If this is the Open Request flow (second time), check if email changed
    if (this.isOpenRequestFlow) {
      // If email hasn't changed, go directly back to OTP verification
      if (this.originalEmail && this.form.email === this.originalEmail) {
        this.currentStep = 'otp';
        return;
      }

      // Email changed or first time, send new OTP
      try {
        await this.sendOtp();
      } catch {
        // UI + GuestForm already handled in sendOtp.
      }
    } else {
      // First time - connect to AI using processGuestData
      this.loading = true;
      PleasedApi.getAnonymousToken()
        .then((res) => {
          this.processGuestData(res);
        })
        .catch((error: unknown) => {
          captureEventException(error, ErrorTrackingEvent.GuestForm);
          this.errorMessage =
            error instanceof Error && error.message
              ? error.message
              : (Locale.i18n.t('global.ticket.ticket_creation_failed.start') as string);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }

  async handleOtpVerification(otpCode: string) {
    // Track OTP verification attempt
    analytics.track({
      name: GENERAL_EVENTS.GUEST_OTP_VERIFY_ATTEMPT,
      metadata: {
        email: this.form.email,
      },
    });

    try {
      reportingService.openRequestClicked();

      const ticketData = {
        email: this.form.email,
        code: otpCode,
        name: this.form.name,
        subject: this.requestInfo?.subject ?? 'Request',
        description: this.form.description,
        userLogs: reportingService.getLogs(),
        chatbotSessionId: analytics.sessionId,
      };

      const response = await PleasedApi.verifyOtpAndCreateTicket(ticketData);
      this.createdTicketId = response.id.toString();

      // Track successful OTP verification and ticket creation
      analytics.track({
        name: GENERAL_EVENTS.GUEST_OTP_VERIFY_SUCCESS,
        metadata: {
          email: this.form.email,
          ticketId: response.id.toString(),
        },
      });

      // Track ticket creation success
      analytics.track({
        name: GENERAL_EVENTS.GUEST_TICKET_CREATED,
        metadata: {
          email: this.form.email,
          ticketId: response.id.toString(),
        },
      });

      // Clear any previous errors
      this.errorMessage = '';

      // Clear guest form data from Vuex store
      this.clearGuestFormData();

      this.currentStep = 'confirmation';
    } catch (error: any) {
      // Track OTP verification failure
      analytics.trackCriticalEvent({
        name: GENERAL_EVENTS.GUEST_OTP_VERIFY_FAILED,
        metadata: {
          email: this.form.email,
          error: error.message || 'Unknown error',
        },
      });
      captureEventException(error, ErrorTrackingEvent.GuestForm);

      throw error;
    }
  }

  goToForm() {
    this.currentStep = 'form';
  }

  handleClose() {
    this.$emit('support-request:cancel', {});
  }

  clearError() {
    this.errorMessage = '';
  }
}
</script>

<style lang="scss" scoped>
.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  padding: 1rem;
  margin: 1rem 0;

  .error-content {
    display: flex;
    align-items: center;

    .error-icon {
      font-size: 1.2rem;
      margin-right: 0.8rem;
      flex-shrink: 0;
      color: #dc2626;
      font-weight: bold;
    }

    .error-text {
      flex: 1;
      color: #dc2626;
      font-size: 0.9rem;
      line-height: 1.4;
    }

    .error-close {
      background: none;
      border: none;
      color: #dc2626;
      font-size: 1.5rem;
      cursor: pointer;
      padding: 0;
      margin-left: 0.8rem;
      flex-shrink: 0;

      &:hover {
        color: #991b1b;
      }
    }
  }
}
</style>
